import { Box } from '@mui/material'
import React, { useMemo } from 'react'

import { FaultIconWithBorder } from 'components/common/FaultIcon'
import GlyphSubIcon from 'components/common/GlyphSubIcon'
import Icon from 'components/Icon'
import SvgHalted from 'components/icons/Halted'
import { getInfraCommandType } from 'components/scenarios/concurrent/attack-attributes'
import { useGetGraphNodeStatusColor } from 'components/scenarios/concurrent/use-get-graph-node-status-color'
import { getStateOfNodeByType } from 'components/scenarios/utils/graph-node-lifecycle'
import {
  isExternalActionNode,
  isFailureFlagNode,
  isRollbackExternalActionNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import { muiTheme } from 'mui-theme'

import type {
  ExternalActionNode,
  FailureFlagNode,
  InfraAttackNode,
  RollbackExternalActionNode
} from 'types/api/scenarios/scenario'

export const GraphNodeGlyphIcon: React.FC<{
  node:
    | InfraAttackNode
    | FailureFlagNode
    | ExternalActionNode
    | RollbackExternalActionNode
}> = ({ node }) => {
  const faultName = useMemo(() => {
    if (isFailureFlagNode(node)) {
      return 'failureFlag'
    }

    if (isExternalActionNode(node) || isRollbackExternalActionNode(node)) {
      return 'awsExternalAction'
    }

    return getInfraCommandType(node)
  }, [node])

  const { isHalted, isSuccessful, isFailed, isNoOp } =
    getStateOfNodeByType(node)

  const { isWarningInsteadOfError, statusBgColor } =
    useGetGraphNodeStatusColor(node)

  return (
    <Box
      sx={(muiTheme) => ({
        marginRight: muiTheme.spacing(2)
      })}
    >
      <FaultIconWithBorder
        faultName={faultName}
        iconColor={muiTheme.palette.text.primary}
      >
        {(isFailed || isHalted || isSuccessful) && (
          <GlyphSubIcon
            css={{
              background: statusBgColor,
              justifyContent: 'center'
            }}
            data-testid={`attack-graph-node-status-icon-bgcolor-${statusBgColor}`}
          >
            {isWarningInsteadOfError ? (
              <Icon css={{ paddingBottom: 1 }} icon="solidWarning" />
            ) : (
              isFailed && <Icon css={{ padding: 1 }} icon="solidClose" />
            )}
            {isHalted && <SvgHalted css={{ padding: 1 }} />}
            {isNoOp && <Icon icon="solidMinus" />}
            {isSuccessful && !isNoOp && <Icon icon="solidCheck" />}
          </GlyphSubIcon>
        )}
      </FaultIconWithBorder>
    </Box>
  )
}
